// @ts-nocheck — verbatim port from xop-edge-staff. See src/crm/README.md.
import { withProspectsAccess } from "@crm-src/components/page-permission-guard"
import usePermissions from "@crm-src/hooks/usePermissions"

import { useState, useEffect, useCallback, useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom";
import { useToast } from "@crm-src/hooks/use-toast"
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  ListSubheader,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material"
import type { ChipProps } from "@mui/material"
import { PageHeader } from "@/components/ui/page-header"
import { PageLoader } from "@/components/ui/page-loader"
import { ConfirmDialog } from "@/components/ui/confirm-dialog"
import { Pagination, paginationPropsFromMeta } from "@/components/ui/pagination"
import {
  UserPlus,
  Search,
  // RESELLER PORT: X is the search box's "Clear search" button (see
  // handleClearSearch below), not a write control. The other four came back
  // with the row Actions menu (spec 2026-09-09 §4.5).
  X,
  MoreHorizontal,
  Eye,
  Edit,
  Trash2,
  TrendingUp,
  Building2,
  Users,
  Sparkles,
} from "lucide-react"

import { StatsCard, ColoredDot, DollarIcon } from "@crm-src/components/stats-card"
import { ProspectFormModal } from "@crm-src/components/prospect-form-modal"
import { leadsApi as leadsApiService, isAiProspect, AI_PROSPECT_SOURCES } from "@crm-src/lib/leads-api"
import { prospectOriginConfig, type ProspectOrigin } from "./prospects-origin"
import { formatIndustry, INDUSTRY_OPTIONS, PROSPECT_INDUSTRIES } from "@crm-src/lib/industries"
import { useResellers } from "@crm-src/hooks/useResellers"
import { useUsers } from "@crm-src/hooks/useUsers"
import { useTableQuery } from "@crm-src/components/table/useTableQuery"
import { useDebouncedSearch } from "@crm-src/components/table/useDebouncedSearch"
import { useLatestRequest } from "@crm-src/components/table/useLatestRequest"
import { buildTableParams } from "@crm-src/components/table/table-query"
import { SortableHeader } from "@crm-src/components/table/SortableHeader"
import { TableFilterBar } from "@crm-src/components/table/TableFilterBar"
import { TableEmptyState } from "@crm-src/components/table/TableEmptyState"
import { PROSPECTS_TABLE } from "@crm-src/components/table/configs"
import type { TableColumn } from "@crm-src/components/table/configs"
import { buildProspectFacetDefs } from "./prospects-page-query"
import type { ProspectFacetKey } from "./prospects-page-query"
import { InlineSalespeopleEditor, InlineSelectEditor } from "@crm-src/components/inline-edit"
import type {
  Lead,
  LeadFilters,
  LeadStats,
  DetailedLead,
  LeadPriority
} from "@crm-src/types/lead"
import { LEAD_PRIORITIES, LEAD_SOURCES } from "@crm-src/types/lead"
import { Money, FinancialOnly, useFinancialAccess, useMoneyFormatter } from "@crm-src/components/money"
import { useResellerAccess } from "@crm-src/components/reseller"
import { ConvertToCustomerDialog } from "@crm-src/components/prospects/convert-to-customer-dialog"

// RESELLER PORT: no import/export and no bulk bar here.
// crm-api refuses BULK writes to a reseller actor whatever permission it
// holds, so each would be a button that 403s. Removed rather than left
// standing — the same call as the Customers World Map button. The modules
// they needed (the bulk modals, ai-prospect-release) are NOT copied into this
// tree, and `releaseAiProspectLeads` is why confirmDeleteLead below carries no
// `cfg.releaseOnDelete` branch: this portal's only origin sets it false.
//
// The PER-ROW write surface is back, since spec 2026-09-09 §4.5: crm-api now
// grants `leads#create`, `#update`, `#destroy` and `#convert` to a reseller
// actor carrying the reseller_permission `prospects:edit`, and stamps a new
// lead's reseller from the actor. So Add Prospect, `prospect-form-modal` and
// the row Actions menu (Edit / Convert / Delete) are back, GATED on that same
// key rather than restored unconditionally — a member without it sees the
// read-only page the port shipped, unchanged. Pinned by ProspectsPage.test.tsx.
// See src/crm/README.md.

/** Rows per page. Must match the `per_page` the request sends and the value
 * the pagination footer falls back to. */
const PER_PAGE = 25

/**
 * The shared prospects list. Rendered by TWO sibling routes, parameterised by
 * `origin` (see `./prospects-origin`):
 *   • origin="regular" → /crm/prospects     (human-entered prospects)
 *   • origin="ai"      → /crm/ai-prospects   (autonomous sales-agent prospects)
 * The origin pins the crm-api `ai_generated` filter server-side, so counts +
 * pagination are per-page. There is no in-page All/Regular/AI toggle anymore —
 * the page IS the filter.
 */
export function ProspectsListPage({ origin }: { origin: ProspectOrigin }) {
  const cfg = prospectOriginConfig(origin)
  const navigate = useNavigate()
  const { toast } = useToast()
  const { isAdmin, hasPermission } = usePermissions()
  // RESELLER PORT: the write controls are back (spec 2026-09-09 §4.5), gated
  // rather than deleted. crm-api's reseller_permission "prospects:edit" is the
  // wall; this only decides whether the control is drawn.
  const canEditProspects = hasPermission("prospects", "edit")
  const { resellers } = useResellers()
  // Reseller options for the inline-edit select. "none" === Direct (no
  // reseller) — the same sentinel the bulk-assign-reseller modal sends.
  const resellerOptions = [
    { value: "none", label: "Direct" },
    ...resellers.map((r) => ({ value: String(r.id), label: r.name })),
  ]
  // Financial wall: hide the money-only "Value" stat card + table column from
  // viewers without financial access (crm:financials). See components/money.tsx.
  const canViewFinancials = useFinancialAccess()
  const formatMoney = useMoneyFormatter()
  // Reseller wall: hide the "Reseller" column + its inline edit + the
  // "Assign Reseller" bulk action from viewers without crm:resellers.
  // See components/reseller.tsx.
  const canViewResellers = useResellerAccess()

  // State management
  const [leads, setLeads] = useState<Lead[]>([])
  const [stats, setStats] = useState<LeadStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isConvertDialogOpen, setIsConvertDialogOpen] = useState(false)
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null)
  const [leadToConvert, setLeadToConvert] = useState<Lead | null>(null)
  const [leadToEdit, setLeadToEdit] = useState<Lead | null>(null)

  // Per-row actions menu anchor (keyed by lead id) — replaces the shadcn
  // DropdownMenu during the MUI 5 migration. RESELLER PORT: the bulk-selection
  // state the source declares beside this stays out (no bulk bar here).
  const [menuAnchor, setMenuAnchor] = useState<{ el: HTMLElement; id: string } | null>(null)

  const searchInputRef = useRef<HTMLInputElement>(null)

  // Sort / facet / page state. The URL is the SINGLE source of truth — there is
  // deliberately no `useState` copy of any of it, so a refresh, the back button
  // and a pasted link all reproduce the same view. See components/table/.
  //
  // Both walls drop their facet from `facetKeys` (not just from the rendered
  // bar), so a hand-typed `?reseller_id=…` is ignored for a viewer who cannot
  // see the Reseller column either. `ai_generated` is never in this list: it is
  // the page's ORIGIN, pinned below, not something the viewer chooses.
  //
  // Annotated, not inferred: TypeScript reads `(k) => k !== "reseller_id"` as a
  // type predicate and would otherwise give the two branches DIFFERENT key
  // unions, which then disagree with the facet defs built from the full set.
  const facetKeys = useMemo<readonly ProspectFacetKey[]>(
    () => (canViewResellers
      ? PROSPECTS_TABLE.facetKeys
      : PROSPECTS_TABLE.facetKeys.filter((k) => k !== "reseller_id")),
    [canViewResellers],
  )
  const table = useTableQuery({
    defaultSort: PROSPECTS_TABLE.defaultSort,
    defaultDir: PROSPECTS_TABLE.defaultDir,
    facetKeys,
  })

  // Only the TEXT box debounces (500ms) — a header or facet click fires at
  // once. The hook also owns the "a new term means page 1 again" reset, which
  // is the part that loops if a page writes it inline.
  const search = useDebouncedSearch(table.setPage)

  const { users } = useUsers()
  const facetDefs = useMemo(
    // Source vocabulary is per-ORIGIN: the AI page's rows carry the agent's
    // marker, never a hand-picked LEAD_SOURCE.
    () => buildProspectFacetDefs(
      resellers,
      users,
      PROSPECT_INDUSTRIES,
      origin === "ai" ? AI_PROSPECT_SOURCES : LEAD_SOURCES,
    ).filter((def) => facetKeys.includes(def.key)),
    [resellers, users, origin, facetKeys],
  )
  // Annotated because the `as const` config elements have no `align` property,
  // so the filtered array would not widen to `TableColumn` on its own.
  const columns = useMemo<readonly TableColumn[]>(
    () => PROSPECTS_TABLE.columns.filter((col) =>
      (canViewResellers || col.key !== "reseller") &&
      (canViewFinancials || col.key !== "value")),
    [canViewResellers, canViewFinancials],
  )

  // Pagination state (what the LAST response reported — the requested page
  // lives in the URL via `table.page`).
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: PER_PAGE
  })

  // The exact params the list request sends. Derived, never stored: sort/dir/
  // facets/page come from `table` (i.e. the URL) and the search term from the
  // debounce above. Also handed to the export button so a CSV matches the
  // filtered view on screen.
  //
  // `buildTableParams` is generic over the page's facet-key union, so the
  // params it returns type-check against `LeadFilters` field by field: a facet
  // key the API does not implement is a compile error here, not a param the
  // server silently drops. No cast.
  const listParams = useMemo<LeadFilters>(() => ({
    ...buildTableParams(
      { sort: table.sort, dir: table.dir },
      table.facets,
      table.page,
      PER_PAGE,
      search.search,
    ),
    include_stats: true,
    // The page IS the origin filter (regular → false, which deliberately KEEPS
    // NULL-source rows; ai → true), so the crm-api returns only this page's
    // prospects with matching counts, pagination AND stat cards.
    //
    // RESELLER PORT: origin="all" pins `aiGenerated: undefined` (see
    // prospects-origin.ts) — a reseller's one list must not filter by
    // source at all. Spreading it in only when defined is what actually
    // omits the param, rather than sending the key with an undefined value.
    ...(cfg.aiGenerated !== undefined ? { ai_generated: cfg.aiGenerated } : {}),
  }), [table.sort, table.dir, table.facets, table.page, search.search, cfg.aiGenerated])

  // Drops a slow earlier response that would otherwise land after a faster
  // later one. See useLatestRequest.
  const beginRequest = useLatestRequest()

  // Load prospects data
  const loadLeads = useCallback(async () => {
    const isCurrent = beginRequest()
    try {
      setLoading(true)
      const response = await leadsApiService.getAll(listParams)
      if (!isCurrent()) return

      setLeads(response.leads || [])
      setPagination(response.pagination || {
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
      if (response.stats) {
        setStats(response.stats)
      }
    } catch (error) {
      if (!isCurrent()) return
      console.error('Error loading prospects:', error)
      toast({
        title: "Error",
        description: "Failed to load prospects",
        variant: "destructive",
      })
      // Set empty data on error
      setLeads([])
      setPagination({
        current_page: 1,
        total_pages: 1,
        total_count: 0,
        per_page: PER_PAGE
      })
    } finally {
      if (isCurrent()) setLoading(false)
    }
  }, [listParams, beginRequest, toast])

  // Initial load and filter changes
  useEffect(() => {
    loadLeads()
  }, [loadLeads])

  // Clear search input
  const handleClearSearch = () => {
    search.clear()
    searchInputRef.current?.focus()
  }

  // Handle prospect created
  const handleProspectCreated = async (prospect: DetailedLead) => {
    // Two-step refresh so the new row shows up the instant the user
    // dismisses the "Add Contacts" follow-up modal:
    //   1. Optimistically splice the new lead in at the top of the
    //      local state — guaranteed-visible without waiting on the API.
    //   2. Send the list back to page 1 with no search, then reload so
    //      the canonical row + server-computed stats/pagination catch up.
    //      The reset is also what makes the row visible if the user
    //      happened to have a search or a later page active.
    setLeads(prev => {
      const filtered = prev.filter(l => l.id !== prospect.id)
      return [prospect as unknown as Lead, ...filtered]
    })
    setPagination(prev => ({ ...prev, total_count: prev.total_count + 1 }))

    search.clear()
    table.setPage(1)

    // Refresh leads list
    await loadLeads()
  }

  // Handle prospect updated
  const handleProspectUpdated = async (updatedProspect: DetailedLead) => {
    // Update the prospect in the list
    setLeads(prevLeads =>
      prevLeads.map(lead =>
        lead.id === updatedProspect.id ? updatedProspect : lead
      )
    )

    // Refresh leads list to get latest data
    await loadLeads()
  }

  // Handle convert to customer
  const handleConvertToCustomer = async (lead: Lead) => {
    setLeadToConvert(lead)
    setIsConvertDialogOpen(true)
  }

  const confirmConvertToDeal = async () => {
    if (!leadToConvert) return
    
    try {
      
      // Call the real conversion API
      const result = await leadsApiService.convert(leadToConvert.id)
      
              const followUpDate = result.follow_up_task?.due_date 
        ? new Date(result.follow_up_task.due_date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
          })
        : 'soon'
      
      toast({
        title: "Success", 
        description: `${leadToConvert.full_name} converted successfully! Created customer contact and follow-up task due ${followUpDate}.`,
      })
      
      setIsConvertDialogOpen(false)
      setLeadToConvert(null)
      
      // Refresh the leads list to show updated status
      loadLeads()
      
      
      // Navigate to the customers page
      
    } catch (error: any) {
      console.error('Error converting to deal:', error)
      
      const errorData = error?.response?.data
      const errorMessage = errorData?.error || 'Failed to convert to customer'
      const suggestions = errorData?.suggestions || []
      
      // Create detailed error message with suggestions
      let description = errorMessage
      if (errorData?.current_status && errorData?.required_status) {
        description += `\n\nCurrent: ${errorData.current_status} → Required: ${errorData.required_status}`
      }
      
      if (suggestions.length > 0) {
        description += `\n\nNext steps:\n• ${suggestions.join('\n• ')}`
      }
      
      // PORTAL DIVERGENCE. The source titles EVERY convert failure "Lead Not
      // Ready for Conversion", which is a diagnosis, not a title: a 403 from
      // the reseller_permission wall or a 404 from Layer 1 then told the user
      // to go fix a status field that is not the problem. The detail page's
      // equivalent (ProspectsDetailPage#handleConvertToCustomer) is honest —
      // title "Error", body from the server — so this follows its shape. The
      // status/suggestion enrichment below the title is kept: it is
      // conditional on the server actually sending those keys, so it adds the
      // real diagnosis exactly when there is one.
      toast({
        title: "Error",
        description: description,
        variant: "destructive",
      })
    }
  }


  // Handle view prospect details
  const handleViewLead = (lead: Lead) => {
    navigate(`/crm/prospects/${lead.id}`)
  }

  // Handle edit prospect
  const handleEditLead = async (lead: Lead) => {
    try {
      const response = await leadsApiService.getById(lead.id)
      const fullLead = response.lead
      
      setLeadToEdit(fullLead)
      setIsEditDialogOpen(true)
    } catch (error) {
      console.error('Error loading prospect for editing:', error)
      toast({
        title: "Error",
        description: "Failed to load prospect details for editing",
        variant: "destructive",
      })
    }
  }

  // Handle delete prospect - open confirmation dialog
  const handleDeleteLead = (lead: Lead) => {
    setLeadToDelete(lead)
    setIsDeleteDialogOpen(true)
  }

  // Confirm delete prospect
  const confirmDeleteLead = async () => {
    if (!leadToDelete) return

    try {
      await leadsApiService.delete(leadToDelete.id)
      // RESELLER PORT: the source follows this with a `cfg.releaseOnDelete`
      // branch that releases the originating Sales-agent lead. This portal's
      // only origin ("all") sets that flag false and `ai-prospect-release` is
      // not in this tree — the branch is dead here, so it is not carried.
      toast({
        title: "Success",
        description: cfg.deleteSuccessMessage,
      })
      setLeadToDelete(null)
      loadLeads()
    } catch (error) {
      console.error('Error deleting prospect:', error)
      toast({
        title: "Error",
        description: "Failed to delete prospect",
        variant: "destructive",
      })
      throw error
    }
  }

  // Cancel delete
  const cancelDelete = () => {
    setIsDeleteDialogOpen(false)
    setLeadToDelete(null)
  }

  // Map a lead priority to a MUI Chip palette color (inlined during the MUI 5
  // migration — was the Tailwind class pair from getLeadPriorityColor).
  const priorityChipColor = (priority: LeadPriority): ChipProps['color'] => {
    switch (priority) {
      case 'low':
        return 'success'
      case 'medium':
        return 'warning'
      case 'high':
        return 'warning'
      case 'urgent':
        return 'error'
      default:
        return 'default'
    }
  }

  // Inline edit (list rows): assign salesperson / change priority right from
  // the table without opening the edit modal. Both throw on failure so the
  // shared <InlineEditField> surfaces the error toast; on success we reload so
  // the canonical row + stats catch up. Gated on isAdmin() to match the bulk
  // actions. See docs/crm.md → "Inline editing".
  const handleInlineAssignSalespeople = async (leadId: string, userIds: string[]) => {
    await leadsApiService.bulkAssignSalespeople([leadId], userIds)
    await loadLeads()
    toast({ title: "Saved", description: "Salesperson updated" })
  }

  const handleInlineChangePriority = async (leadId: string, priority: LeadPriority) => {
    await leadsApiService.update(leadId, { priority })
    await loadLeads()
    toast({ title: "Saved", description: "Priority updated" })
  }

  const handleInlineChangeIndustry = async (leadId: string, industry: string) => {
    await leadsApiService.update(leadId, { industry })
    await loadLeads()
    toast({ title: "Saved", description: "Industry updated" })
  }

  // Reseller assignment reuses the bulk endpoint (bulk-of-one); "none" === Direct.
  //
  // NOT DEAD CODE, despite reading like it in this portal: its call site is the
  // Reseller cell's InlineSelectEditor below, inside `{canViewResellers && ...}`.
  // `useResellerAccess()` is false for a reseller actor, so the cell — and this
  // handler with it — never renders here, which is why the README can say the
  // bulk-assign-reseller surface is never mounted on this page. Deleting the
  // handler would leave that ported cell referencing an undefined name.
  const handleInlineAssignReseller = async (leadId: string, resellerId: string) => {
    await leadsApiService.bulkAssignReseller([leadId], resellerId)
    await loadLeads()
    toast({ title: "Saved", description: "Reseller updated" })
  }

  return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Header. The AI Prospects page is manage-only — humans don't
              hand-create AI prospects (the sales agent does, via Convert to
              Prospect), so Import + Add are hidden there (cfg.allowCreate).
              This portal's "all" origin sets allowCreate: true (a reseller's
              list IS hand-created), same shape as the source. */}
          <PageHeader
            title={cfg.title}
            description={cfg.description}
            actions={
              // RESELLER PORT: gated on cfg.allowCreate (the source's own
              // condition) AND prospects:edit — the latter is this portal's
              // addition, since "may this LIST take a hand-created prospect"
              // and "may THIS member create one" are different questions.
              // Import/Export stays out either way (crm-api refuses it).
              cfg.allowCreate && canEditProspects ? (
                <Button
                  variant="contained"
                  startIcon={<UserPlus size={16} />}
                  onClick={() => setIsAddDialogOpen(true)}
                >
                  Add Prospect
                </Button>
              ) : undefined
            }
          />

          {/* Add Prospect Modal. The form's reseller picker is wrapped in
              <ResellerOnly>, which renders nothing here — so the UI cannot
              express "attach this to someone else", and crm-api stamps the
              reseller from the actor regardless. Two independent guarantees. */}
          {canEditProspects && (
            <>
              <ProspectFormModal
                isOpen={isAddDialogOpen}
                onClose={() => setIsAddDialogOpen(false)}
                onSuccess={handleProspectCreated}
              />

              {/* The SAME modal, opened with a prospect — that is what makes
                  it the edit form. The source mounts both instances too. */}
              <ProspectFormModal
                isOpen={isEditDialogOpen}
                onClose={() => {
                  setIsEditDialogOpen(false)
                  setLeadToEdit(null)
                }}
                prospect={leadToEdit as any}
                onSuccess={handleProspectUpdated}
              />
            </>
          )}

          {/* Stats Cards */}
          {stats && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }, gap: 2 }}>
              <StatsCard
                title="Total Prospects"
                value={stats.total_prospects}
                icon={UserPlus}
                iconColor="text-blue-600"
              />
              {/* Financial wall: the "Total Value" tile is money-only — drop it
                  entirely (not "—") for viewers without financial access. The
                  string formatter is safe here because the card only renders
                  when access is granted. */}
              <FinancialOnly>
                <StatsCard
                  title="Total Value"
                  value={formatMoney(stats.total_prospected_value)}
                  customIcon={<DollarIcon />}
                />
              </FinancialOnly>
              <StatsCard
                title="High Priority"
                value={stats.high_priority_prospects}
                valueColor="text-red-600"
                customIcon={<ColoredDot color="bg-red-100" />}
              />
              <StatsCard
                title="Urgent Priority"
                value={stats.urgent_priority_prospects}
                valueColor="text-orange-600"
                customIcon={<ColoredDot color="bg-orange-100" />}
              />
            </Box>
          )}

          {/* Prospects Table */}
          <Card>
            <CardContent>
              {/* Controls row: the search box is WRAPPED, not replaced — same
                  field, same 500ms debounce, same clear button; the facets simply
                  sit beside it, left-aligned. The old All / Regular / AI-generated
                  origin segments are gone — regular and AI prospects now live on
                  two separate pages (/crm/prospects vs /crm/ai-prospects), so the
                  page itself is the origin filter and it is NOT a facet. No card
                  title/subtitle either — the page header already carries the title. */}
              <Box sx={{ mb: 2 }}>
                <TableFilterBar
                  facetDefs={facetDefs}
                  values={table.facets}
                  onChange={table.setFacet}
                  onClearAll={table.clearFacets}
                  searchSlot={
                    <TextField
                      inputRef={searchInputRef}
                      placeholder={cfg.searchPlaceholder}
                      value={search.input}
                      onChange={(e) => search.setInput(e.target.value)}
                      size="small"
                      sx={{ width: { xs: "100%", sm: 320 }, flexShrink: 0 }}
                      autoComplete="off"
                      name="prospect-search"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box sx={{ display: "flex", color: search.isSearching ? "primary.main" : "text.disabled" }}><Search size={16} /></Box>
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            {search.input && !search.isSearching && (
                              <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                                <X size={16} />
                              </IconButton>
                            )}
                            {search.isSearching && (
                              <Typography component="span" sx={{ fontSize: "0.875rem", color: "primary.main" }}>
                                Searching...
                              </Typography>
                            )}
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                />
              </Box>

              {loading ? (
                <PageLoader />
              ) : leads.length === 0 ? (
                // Names whichever narrowing actually emptied the list: filtering
                // to nothing used to claim there were no prospects at all. The
                // genuinely-empty copy stays this PAGE's (the AI page explains
                // where AI prospects come from).
                <TableEmptyState
                  entityNoun="prospects"
                  hasSearch={search.input.trim().length > 0}
                  activeFilterCount={table.activeFilterCount}
                  onClearFilters={table.clearFacets}
                  emptyTitle={cfg.emptyTitle}
                  emptyBody={cfg.emptyBody}
                />
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        {/* The eight DATA columns, in `PROSPECTS_TABLE` order —
                            which is the order the body cells below are written in,
                            so the two must be edited together. Both walls filter
                            `columns`: "Reseller" for viewers without crm:resellers
                            and the money-only "Value" for viewers without
                            crm:financials (their body cells are conditional in
                            lockstep). The checkbox and actions cells either side
                            stay plain <TableCell>s: neither maps to a server
                            column, so neither can sort. */}
                        {columns.map((col) => (
                          <SortableHeader
                            key={col.key}
                            column={col.key}
                            label={col.label}
                            sort={table.sort}
                            dir={table.dir}
                            onSort={table.toggleSort}
                            {...(col.align ? { align: col.align } : {})}
                            {...(col.key === "code" ? { sx: { whiteSpace: "nowrap" } } : {})}
                          />
                        ))}
                        <TableCell sx={{ width: 50 }}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {leads.map((lead) => (
                        <TableRow
                          key={lead.id}
                          hover
                        >
                          <TableCell
                            sx={{ cursor: "pointer", whiteSpace: "nowrap" }}
                            onClick={() => handleViewLead(lead)}
                          >
                            <Box component="span" sx={{ display: "inline-block", whiteSpace: "nowrap", fontSize: "0.75rem", fontWeight: 600, color: "text.secondary", bgcolor: "action.hover", px: 0.75, py: 0.25, borderRadius: 1 }}>
                              {lead.customer_id || lead.id}
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{ cursor: "pointer" }}
                            onClick={() => handleViewLead(lead)}
                          >
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0, maxWidth: 260 }}>
                              <Typography sx={{ fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lead.company_name}</Typography>
                              {isAiProspect(lead) && (
                                <Chip
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                  icon={<Sparkles size={12} />}
                                  label="AI"
                                  title="AI-generated prospect — created by the autonomous sales agent"
                                  sx={{ height: 20, flexShrink: 0, "& .MuiChip-label": { px: 0.5, fontSize: "0.65rem", fontWeight: 600 }, "& .MuiChip-icon": { ml: 0.5 } }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <InlineSelectEditor
                              canEdit={isAdmin()}
                              value={lead.industry ?? ""}
                              options={INDUSTRY_OPTIONS}
                              onSave={(v) => handleInlineChangeIndustry(lead.id, v)}
                              ariaLabel="Edit industry"
                              errorMessage="Failed to update industry"
                            >
                              {lead.industry ? (
                                <Chip size="small" variant="outlined" label={formatIndustry(lead.industry)} sx={{ height: 22, "& .MuiChip-label": { px: 1, fontSize: "0.75rem" } }} />
                              ) : (
                                <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.disabled" }}>—</Typography>
                              )}
                            </InlineSelectEditor>
                          </TableCell>
                          {/* Reseller wall: drop the "Reseller" cell (must stay
                              in lockstep with the header above). */}
                          {canViewResellers && (
                            <TableCell>
                              <InlineSelectEditor
                                canEdit={isAdmin()}
                                value={lead.reseller ? String(lead.reseller.id) : "none"}
                                options={resellerOptions}
                                onSave={(v) => handleInlineAssignReseller(lead.id, v)}
                                ariaLabel="Edit reseller"
                                errorMessage="Failed to update reseller"
                              >
                                {lead.reseller ? (
                                  <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>{lead.reseller.name}</Typography>
                                ) : (
                                  <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Direct</Typography>
                                )}
                              </InlineSelectEditor>
                            </TableCell>
                          )}
                          <TableCell>
                            <InlineSalespeopleEditor
                              canEdit={isAdmin()}
                              value={(lead.assigned_salespeople ?? []).map((s) => s.user_id)}
                              onSave={(ids) => handleInlineAssignSalespeople(lead.id, ids)}
                            >
                              {lead.assigned_salespeople && lead.assigned_salespeople.length > 0 ? (
                                <Box>
                                  <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary" }}>
                                    {lead.salespeople_names || lead.assigned_salespeople[0].full_name}
                                  </Typography>
                                  {lead.assigned_salespeople.length > 1 && (
                                    <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
                                      +{lead.assigned_salespeople.length - 1} more
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>Unassigned</Typography>
                              )}
                            </InlineSalespeopleEditor>
                          </TableCell>
                          <TableCell>
                            <InlineSelectEditor
                              canEdit={isAdmin()}
                              value={lead.priority}
                              options={LEAD_PRIORITIES.map((p) => ({ value: p.value, label: p.label }))}
                              onSave={(v) => handleInlineChangePriority(lead.id, v as LeadPriority)}
                              ariaLabel="Edit priority"
                              errorMessage="Failed to update priority"
                            >
                              <Chip size="small" color={priorityChipColor(lead.priority) ?? "default"} label={lead.priority} />
                            </InlineSelectEditor>
                          </TableCell>
                          {/* Financial wall: omit the money cell to keep the row
                              aligned with the dropped "Value" header. */}
                          {canViewFinancials && (
                            // Right-aligned to sit under the "Value" header,
                            // which `PROSPECTS_TABLE` declares align:"right".
                            <TableCell align="right">
                              <Typography component="span" sx={{ fontWeight: 500, color: "success.main" }}>
                                <Money value={lead.prospected_value} />
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography component="span" sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                              {new Date(lead.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit'
                              })}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {/* RESELLER PORT: View is UNCONDITIONAL — a reader
                                still opens a row — so it stays its own button
                                rather than the source's "View Details" menu
                                item. The write entries came back with spec
                                2026-09-09 §4.5, gated on prospects:edit. */}
                            <IconButton
                              size="small"
                              sx={{ width: 32, height: 32 }}
                              aria-label="View details"
                              onClick={() => handleViewLead(lead)}
                            >
                              <Eye size={16} />
                            </IconButton>
                            {canEditProspects && (
                              <>
                                <IconButton
                                  size="small"
                                  sx={{ width: 32, height: 32 }}
                                  aria-label="Open menu"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setMenuAnchor({ el: e.currentTarget, id: lead.id })
                                  }}
                                >
                                  <MoreHorizontal size={16} />
                                </IconButton>
                                <Menu
                                  anchorEl={menuAnchor?.id === lead.id ? menuAnchor.el : null}
                                  open={menuAnchor?.id === lead.id}
                                  onClose={() => setMenuAnchor(null)}
                                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                                >
                                  <ListSubheader sx={{ lineHeight: 2, bgcolor: "transparent" }}>Actions</ListSubheader>
                                  <Divider />
                                  <MenuItem
                                    onClick={() => {
                                      setMenuAnchor(null)
                                      handleEditLead(lead)
                                    }}
                                  >
                                    <Box sx={{ mr: 1, display: "flex" }}><Edit size={16} /></Box>
                                    Edit Prospect
                                  </MenuItem>
                                  <Divider />
                                  <MenuItem
                                    onClick={() => {
                                      setMenuAnchor(null)
                                      handleConvertToCustomer(lead)
                                    }}
                                    sx={{ color: "success.main" }}
                                    disabled={lead.status === 'converted'}
                                  >
                                    <Box sx={{ mr: 1, display: "flex" }}><TrendingUp size={16} /></Box>
                                    Convert to Customer
                                  </MenuItem>
                                  <Divider />
                                  <MenuItem
                                    sx={{ color: "error.main" }}
                                    onClick={() => {
                                      setMenuAnchor(null)
                                      handleDeleteLead(lead)
                                    }}
                                  >
                                    <Box sx={{ mr: 1, display: "flex" }}><Trash2 size={16} /></Box>
                                    {cfg.deleteActionLabel}
                                  </MenuItem>
                                </Menu>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Pagination */}
              <Pagination
                {...paginationPropsFromMeta(pagination)}
                entityNoun="prospects"
                hideIfSinglePage
                onPageChange={table.setPage}
              />
            </CardContent>
          </Card>

          {/* The convert + delete dialogs the menu drives. Mounted under the
              same gate as the menu, so a reader never carries them at all. */}
          {canEditProspects && (
            <>
              {/* Convert to Customer Dialog */}
              <ConvertToCustomerDialog
                open={isConvertDialogOpen}
                onOpenChange={setIsConvertDialogOpen}
                lead={leadToConvert}
                onConfirm={confirmConvertToDeal}
              />

              {/* Delete Confirmation Dialog */}
              <ConfirmDialog
                isOpen={isDeleteDialogOpen}
                onClose={cancelDelete}
                title={cfg.deleteTitle}
                description={cfg.deleteDescription}
                confirmLabel={cfg.deleteConfirmLabel}
                onConfirm={confirmDeleteLead}
              >
                {leadToDelete && (
                  <>
                    <Box sx={{ borderRadius: 1, border: 1, borderColor: "divider", bgcolor: "surfaceSoft", p: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {leadToDelete.full_name}
                          </Typography>
                          <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {leadToDelete.email}
                          </Typography>
                          {leadToDelete.company_name && (
                            <Typography sx={{ fontSize: "0.875rem", color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {leadToDelete.company_name}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ flexShrink: 0 }}>
                          <Chip size="small" color="default" label={`${leadToDelete.priority} priority`} />
                        </Box>
                      </Box>
                    </Box>
                    <Typography sx={{ mt: 2, fontSize: "0.875rem", color: "text.secondary" }}>
                      Are you sure you want to delete <strong>{leadToDelete.full_name}</strong>?
                      This will hide the prospect from your active list but preserve all data for future reference.
                    </Typography>
                  </>
                )}
              </ConfirmDialog>
            </>
          )}
        </Box>
  )
}

// /crm/prospects — the regular (human-entered) prospects list.
function ProspectsPage() {
  return <ProspectsListPage origin="regular" />
}

export default withProspectsAccess(ProspectsPage)
